import { Box, HStack, Pressable, Text, VStack } from 'native-base'
import { useState } from 'react'

import { CaretCircleDown, Clock } from '@phosphor-icons/react'

import { AddressHours, DayOfWeek } from '@consumer/graphql'
import { colors } from '../../../utils/theme/configureTheme'
import AddEditBox from '../Common/AddEditBox'

// Animation styles
const animationStyles = `
  @keyframes fadeInSlideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInSlideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes expandFromDot {
    from {
      opacity: 0;
      clip-path: inset(0 100% 0 0);
    }
    to {
      opacity: 1;
      clip-path: inset(0 0 0 0);
    }
  }

  @keyframes expandSuffix {
    from {
      opacity: 0;
      width: 0;
      clip-path: inset(0 100% 0 0);
    }
    to {
      opacity: 1;
      width: 100px;
      clip-path: inset(0 0 0 0);
    }
  }

  @keyframes dotMoveToRight {
    from {
      transform: translateX(0) translateY(-8px);
    }
    to {
      transform: translateX(0) translateY(0);
    }
  }

  @keyframes dotMoveAndExpand {
    from {
      transform: translateX(0);
      opacity: 1;
      background-color: #A7CEEB;
    }
    60% {
      opacity: 1;
      background-color: #21211F;
    }
    to {
      transform: translateX(var(--dot-distance, 110px));
      opacity: 0;
      background-color: #21211F;
    }
  }

  @keyframes dotMoveAndExpandClosed {
    0% {
      transform: translateX(0);
      opacity: 0;
      background-color: transparent;
    }
    60% {
      opacity: 1;
      background-color: #C3C3BE;
    }
    to {
      transform: translateX(var(--dot-distance, 110px));
      opacity: 0;
      background-color: #C3C3BE;
    }
  }
`

interface BusinessHoursDisplayProps {
  addressHours: AddressHours
  normalizedAddressId: string
  orderId?: string
  trackingId?: string
  onEdit?: () => void
  canEdit: boolean
}

// Helper to get display name for day of week
const getDayDisplayName = (day: DayOfWeek): string => {
  const dayMap: Record<DayOfWeek, string> = {
    [DayOfWeek.Monday]: 'MON',
    [DayOfWeek.Tuesday]: 'TUE',
    [DayOfWeek.Wednesday]: 'WED',
    [DayOfWeek.Thursday]: 'THU',
    [DayOfWeek.Friday]: 'FRI',
    [DayOfWeek.Saturday]: 'SAT',
    [DayOfWeek.Sunday]: 'SUN',
  }
  return dayMap[day]
}

// Helper to get full day name
const getFullDayName = (day: DayOfWeek): string => {
  const dayMap: Record<DayOfWeek, string> = {
    [DayOfWeek.Monday]: 'MONDAY',
    [DayOfWeek.Tuesday]: 'TUESDAY',
    [DayOfWeek.Wednesday]: 'WEDNESDAY',
    [DayOfWeek.Thursday]: 'THURSDAY',
    [DayOfWeek.Friday]: 'FRIDAY',
    [DayOfWeek.Saturday]: 'SATURDAY',
    [DayOfWeek.Sunday]: 'SUNDAY',
  }
  return dayMap[day]
}

// Helper to get day name suffix (part after abbreviation)
const getDaySuffix = (day: DayOfWeek): string => {
  const abbreviation = getDayDisplayName(day)
  const fullName = getFullDayName(day)
  return fullName.substring(abbreviation.length)
}

// Calculate approximate dot travel distance based on day name length
// Longer names = less travel distance, shorter names = more travel distance
const getDotTravelDistance = (day: DayOfWeek): string => {
  const fullName = getFullDayName(day)
  const baseDistance = 150 // Base distance for shortest names
  const nameWidth = fullName.length * 6.5 // ~6.5px per character
  return `${baseDistance - nameWidth}px` // Subtract day name width
}

// Convert 24-hour format to 12-hour format (e.g., "0800" -> "8:00AM")
const format24To12Hour = (time24: string): string => {
  // Handle both "0800" and "08:00" formats
  const timeStr = time24.includes(':') ? time24.replace(':', '') : time24
  const hours = parseInt(timeStr.slice(0, 2), 10)
  const minutes = timeStr.slice(2)
  const h = hours % 12 === 0 ? 12 : hours % 12
  const ampm = hours < 12 ? 'AM' : 'PM'
  return `${h}:${minutes}${ampm}`
}

// Order days: Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday
const daysOrder: DayOfWeek[] = [
  DayOfWeek.Sunday,
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
]

/** Displays the business hours for a delivery address with animated expand/collapse behaviour. */
export default function BusinessHoursDisplay({ addressHours, onEdit, canEdit }: BusinessHoursDisplayProps) {
  // Animation states: 0 = horizontal, 1 = vertical unexpanded, 2 = vertical semi-expanded, 3 = fully expanded
  const [animationStage, setAnimationStage] = useState(0)

  // Inject animation styles
  if (typeof document !== 'undefined' && !document.getElementById('business-hours-animations')) {
    const styleSheet = document.createElement('style')
    styleSheet.id = 'business-hours-animations'
    styleSheet.textContent = animationStyles
    document.head.appendChild(styleSheet)
  }

  // Create a map of day hours for easy lookup
  const dayHoursMap = new Map<DayOfWeek, { isClosed: boolean; openTime?: string; closeTime?: string }>()
  if (addressHours.dayHours) {
    addressHours.dayHours.forEach(dayHour => {
      if (dayHour) {
        dayHoursMap.set(dayHour.dayOfWeek, {
          isClosed: dayHour.isClosed,
          openTime: dayHour.openTime ?? undefined,
          closeTime: dayHour.closeTime ?? undefined,
        })
      }
    })
  }

  const handleToggle = () => {
    if (animationStage === 2) {
      // Collapsing: go directly to horizontal
      setAnimationStage(0)
    } else {
      // Expanding: progress through stages
      setAnimationStage(1) // First go to vertical unexpanded
      setTimeout(() => {
        setAnimationStage(2) // Then to fully expanded (with animation in component)
      }, 400)
    }
  }

  const isExpanded = animationStage === 2
  const isTransitioning = animationStage > 0

  return (
    <AddEditBox label="business hours" ContentIcon={Clock} canEdit={canEdit} onEdit={onEdit}>
      <HStack
        justifyContent="space-between"
        alignItems={isTransitioning ? 'flex-start' : 'center'}
        flexWrap="wrap"
        space={isTransitioning ? 6 : 0}
      >
        {/* Stage 0: Horizontal Unexpanded View */}
        {animationStage === 0 && <HorizontalUnexpandedView dayHoursMap={dayHoursMap} />}

        {/* Stage 1: Vertical Unexpanded View (abbreviated days with dots) */}
        {animationStage === 1 && <VerticalUnexpandedView dayHoursMap={dayHoursMap} />}

        {/* Stage 2: Fully Expanded View (full day names with dots→times animation) */}
        {animationStage === 2 && <VerticalSemiexpandedView dayHoursMap={dayHoursMap} />}

        {/* Caret - always rendered */}
        <Pressable onPress={handleToggle}>
          <Box
            style={
              {
                transition: 'transform 0.3s ease-out',
                transform: isExpanded || isTransitioning ? 'rotate(180deg)' : 'rotate(0deg)',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
          >
            <CaretCircleDown size={24} color={colors.brand.softBlack} weight="fill" />
          </Box>
        </Pressable>
      </HStack>
    </AddEditBox>
  )
}

export const HorizontalUnexpandedView = ({
  dayHoursMap,
}: {
  dayHoursMap: Map<DayOfWeek, { isClosed: boolean; openTime?: string; closeTime?: string }>
}) => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Box style={{ animation: 'fadeInSlideUp 0.3s ease-out' } as any}>
      <HStack px={3} space={4} alignItems="flex-end" flexWrap="wrap">
        {daysOrder.map(day => {
          const dayHours = dayHoursMap.get(day)
          const hasHours = dayHours && !dayHours.isClosed
          return (
            <VStack key={day} alignItems="center" space={0}>
              <Box
                width={1.5}
                height={1.5}
                borderRadius="full"
                bg={hasHours ? colors.brand.selectedBlue : 'transparent'}
                mb={0}
              />
              <Text
                fontSize={10}
                fontWeight={hasHours ? '700' : '400'}
                fontFamily="bodyBold"
                lineHeight="125%"
                letterSpacing="2%"
                color={hasHours ? colors.brand.softBlack : colors.gray[100]}
                textAlign="center"
              >
                {getDayDisplayName(day)}
              </Text>
            </VStack>
          )
        })}
      </HStack>
    </Box>
  )
}

export const VerticalUnexpandedView = ({
  dayHoursMap,
}: {
  dayHoursMap: Map<DayOfWeek, { isClosed: boolean; openTime?: string; closeTime?: string }>
}) => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Box style={{ animation: 'fadeInSlideDown 0.3s ease-out' } as any}>
      <VStack px={3} pt={2} space={2} alignItems="flex-start" flexWrap="wrap">
        {daysOrder.map(day => {
          const dayHours = dayHoursMap.get(day)
          const hasHours = dayHours && !dayHours.isClosed
          return (
            <HStack key={day} alignItems="center" space={0.5}>
              <Text
                fontSize={10}
                fontWeight={hasHours ? '700' : '400'}
                fontFamily="bodyBold"
                lineHeight="125%"
                letterSpacing="2%"
                color={hasHours ? colors.brand.softBlack : colors.gray[100]}
                textAlign="center"
              >
                {getDayDisplayName(day)}
              </Text>
              <Box
                width={1.5}
                height={1.5}
                borderRadius="full"
                bg={hasHours ? colors.brand.selectedBlue : 'transparent'}
              />
            </HStack>
          )
        })}
      </VStack>
    </Box>
  )
}

export const VerticalSemiexpandedView = ({
  dayHoursMap,
}: {
  dayHoursMap: Map<DayOfWeek, { isClosed: boolean; openTime?: string; closeTime?: string }>
}) => {
  return (
    <VStack pt={2} pl={3} space={2} alignItems="flex-start" flex={1}>
      {daysOrder.map((day, index) => {
        const dayHours = dayHoursMap.get(day)
        const hasHours = dayHours && !dayHours.isClosed
        const openTime = dayHours?.openTime
        const closeTime = dayHours?.closeTime
        const rowDelay = index * 0.08 // 80ms delay between each row

        return (
          <HStack key={day} alignItems="center" justifyContent="space-between" width="100%">
            <HStack alignItems="center" space={0.5}>
              <HStack alignItems="baseline" space={0}>
                {/* Static abbreviation */}
                <Text
                  fontSize={10}
                  fontWeight={'700'}
                  fontFamily="bodyBold"
                  lineHeight="125%"
                  letterSpacing="2%"
                  color={hasHours ? colors.brand.softBlack : colors.gray[100]}
                  textAlign="left"
                >
                  {getDayDisplayName(day)}
                </Text>
                {/* Expanding suffix */}
                <Box
                  style={
                    {
                      animation: 'expandSuffix 0.3s ease-out',
                      animationDelay: `${rowDelay}s`,
                      display: 'inline-flex',
                      overflow: 'hidden',
                      alignItems: 'baseline',
                      opacity: 0,
                      animationFillMode: 'forwards',
                      width: 0,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any
                  }
                >
                  <Text
                    fontSize={10}
                    fontWeight={'700'}
                    fontFamily="bodyBold"
                    lineHeight="125%"
                    letterSpacing="2%"
                    color={hasHours ? colors.brand.softBlack : colors.gray[100]}
                    textAlign="left"
                    // style={{ whiteSpace: 'nowrap' }}
                  >
                    {getDaySuffix(day)}
                  </Text>
                </Box>
              </HStack>
              {/* Dot that moves to the right */}
              <Box
                style={
                  {
                    '--dot-distance': getDotTravelDistance(day),
                    transform: 'translateX(0)',
                    animation: hasHours
                      ? 'dotMoveAndExpand 0.5s ease-out forwards'
                      : 'dotMoveAndExpandClosed 0.5s ease-out forwards',
                    animationDelay: `${rowDelay}s`,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any
                }
                width={1.5}
                height={1.5}
                borderRadius="full"
                bg={hasHours ? colors.brand.selectedBlue : 'transparent'}
              />
            </HStack>
            {/* Time text that expands from dot position */}
            <Box
              style={
                {
                  animation: 'expandFromDot 0.4s ease-out forwards',
                  animationDelay: `${rowDelay + 0.25}s`,
                  opacity: 0,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any
              }
            >
              <Text
                fontSize={10}
                fontWeight={'700'}
                fontFamily={'bodyBold'}
                lineHeight="125%"
                letterSpacing="2%"
                color={hasHours ? colors.brand.softBlack : colors.gray[100]}
              >
                {hasHours && openTime && closeTime
                  ? `${format24To12Hour(openTime)} - ${format24To12Hour(closeTime)}`
                  : 'CLOSED'}
              </Text>
            </Box>
          </HStack>
        )
      })}
    </VStack>
  )
}
